import { add, dec, div, fromDouble, gt, max, mul, sub, toDouble, ZERO, type Decimal } from '../core/decimal.ts';

export interface RetirementInputs {
  readonly retirementAge: number;
  readonly currentAge: number;
  readonly monthlyNeed: Decimal;
  readonly workplacePension: Decimal;
  readonly statePension: Decimal;
  readonly otherIncome: Decimal;
  readonly currentInvestments: Decimal;
}

export interface Retirement {
  readonly yearsUntilRetirement: number;
  readonly guaranteedMonthlyIncome: Decimal;
  readonly monthlyGap: Decimal;
  readonly monthlySurplus: Decimal;
  readonly requiredPot: Decimal;
  readonly shortfall: Decimal;
  /** Double, capped at 100. */
  readonly progressPercent: number;
}

/** `static let defaultWithdrawalRate: Decimal = 0.04` — a float literal, so Decimal(Double 0.04). */
const WITHDRAWAL_RATE = fromDouble(0.04);

/** RetirementCalculation: the pot that funds the monthly gap via the 4% rule. */
export function calculateRetirement(inputs: RetirementInputs): Retirement {
  const guaranteedMonthlyIncome = add(add(inputs.workplacePension, inputs.statePension), inputs.otherIncome);
  const monthlyGap = max(sub(inputs.monthlyNeed, guaranteedMonthlyIncome), ZERO);
  const monthlySurplus = max(sub(guaranteedMonthlyIncome, inputs.monthlyNeed), ZERO);
  const requiredPot = gt(monthlyGap, ZERO) ? div(mul(monthlyGap, dec(12)), WITHDRAWAL_RATE) : ZERO;
  const progressPercent = gt(requiredPot, ZERO)
    ? Math.min((toDouble(inputs.currentInvestments) / toDouble(requiredPot)) * 100, 100)
    : 0;
  return {
    yearsUntilRetirement: Math.max(inputs.retirementAge - inputs.currentAge, 0),
    guaranteedMonthlyIncome,
    monthlyGap,
    monthlySurplus,
    requiredPot,
    shortfall: max(sub(requiredPot, inputs.currentInvestments), ZERO),
    progressPercent,
  };
}

/** RetirementCalculation.defaultStatePension(currencyCode:). */
export function defaultStatePension(currencyCode: string): Decimal {
  switch (currencyCode) {
    case 'GBP':
      return dec(960);
    case 'USD':
      return dec(1900);
    default:
      return ZERO;
  }
}
